import { Component, type ReactNode } from 'react';

type State = { err: Error | null; retries: number };
type Props = { children: ReactNode; fallback?: (err: Error, reset: () => void) => ReactNode };

// Catches render-time crashes. Transient errors (a stale query, a load race) get ONE
// automatic retry after a beat, so the page self-heals instead of showing a scary box.
// A genuinely persistent error shows a clean "hit a snag / Reload" message (no stack trace).
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { err: null, retries: 0 };
  private timer: any = null;

  static getDerivedStateFromError(err: Error) { return { err }; }

  componentDidCatch(err: Error, info: any) {
    // eslint-disable-next-line no-console
    console.error('FiberForge ErrorBoundary caught:', err, info);
    if (this.state.retries < 1) {
      this.timer = setTimeout(() => this.setState((s) => ({ err: null, retries: s.retries + 1 })), 500);
    }
  }
  componentWillUnmount() { if (this.timer) clearTimeout(this.timer); }
  reset = () => this.setState({ err: null });

  render() {
    if (this.state.err) {
      if (this.state.retries < 1) {
        return <div style={{ padding: 48, textAlign: 'center', color: '#64748b', fontSize: 14 }}>Loading…</div>;
      }
      if (this.props.fallback) return this.props.fallback(this.state.err, this.reset);
      return (
        <div style={{ padding: 24 }}>
          <div style={{ background: '#fff7ed', color: '#9a3412', border: '1px solid #fed7aa', padding: 20, borderRadius: 8, maxWidth: 520, margin: '40px auto', textAlign: 'center' }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>This page hit a snag.</div>
            <div style={{ fontSize: 13, marginBottom: 14 }}>A quick reload usually clears it.</div>
            <button onClick={() => location.reload()} className="btn">Reload</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
